import { IAddress, isUS } from 'src/types/address';
import { IFieldErrors } from 'src/types';
import { usStateOptions } from 'src/constants';
import { styled } from '@mui/system';
import { Grid } from '@mui/material';
import { Dropdown, Typography } from 'src/components';
import { Input } from '@cb/apricot-react';
import { FieldLabel} from 'src/generic/component';
import { useEffect, useState } from 'react';
import { get } from 'lodash';
import { diff } from 'deep-object-diff';
import { useReferences } from 'src/services';

const FieldRow = styled(Grid)`
  margin-bottom:10px;
`;

interface AddressFieldProps {
  readOnly?: boolean;
  address?: Partial<IAddress>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Partial<IAddress>;
  showDiff?: boolean;
  onUpdate?: (address: Partial<IAddress>) => void;
  additionalInfo?: boolean ;
  customLabels?: any;
}

export const AddressFields = ({ additionalInfo,customLabels,readOnly, errors, address, original, showDiff, onUpdate }: AddressFieldProps) => {
  // const [data, setData] = useState<Partial<IAddress>>(address || {});
  const { countries } = useReferences() || {}
  const [streetAddr1, setAddress] = useState<string>(address?.streetAddr1 || '');
  const [streetAddr2, setAddress2] = useState<string>(address?.streetAddr2 || '');
  const [streetAddr3, setAddress3] = useState<string>(address?.streetAddr3 || '');
  const [countryIsoCd, setCountry] = useState<string>(address?.countryIsoCd || '');
  const [city, setCity] = useState<string>(address?.city || '');

  const [stateCd, setState] = useState<string>(address?.stateCd || '');
  const [zip5, setZip5] = useState<string>(address?.zip5 || '');
  const [zip4, setZip4] = useState<string>(address?.zip4 || '');

  const [province, setProvince] = useState<string>(address?.province || '');
  const [intlPostalCd, setIntlPostalCd] = useState<string>(address?.intlPostalCd || '');
  const [label, setLabel] = useState<string | undefined>(address?.label);
  const [contactName, setContactName] = useState<string | undefined>(address?.contactName);
  const [contactPhone, setContactPhone] = useState<string | undefined>(address?.contactPhone);

  const getData = (): Partial<IAddress> => {
    const base = {
      streetAddr1,
      streetAddr2,
      streetAddr3,
      countryIsoCd,
      city,
      label,
      contactName,
      contactPhone,
    };

    if (isUS(countryIsoCd)) {
      return {
        ...base,
        stateCd,
        zip5,
        zip4
      };
    } else {
      return {
        ...base,
        province,
        intlPostalCd
      };
    }
  };


  if (showDiff && !original) throw new Error("Must send the original value when comparing differences.");
  const changes: Partial<IAddress> = diff(original || {}, getData());

  const getChange = (field: keyof IAddress): any => {
    return isChanged(field) ? changes[field] : null;
  };

  const getCurrent = (field: keyof IAddress): any => {
    const existing = original || {};
    return existing[field];
  };

  const isSet = (field: keyof IAddress): boolean => {
    return (field in (original || {}));
  };

  const isChanged = (field: keyof IAddress): boolean => {
    return (field in (changes));
  };

  const getChangeColor = (field: keyof IAddress): 'cb-removed' | 'cb-modified' | 'cb-added' | undefined => {
    const set = isSet(field);
    const changed = isChanged(field);

    if (!changed) {
      return undefined;
    }

    const change = getChange(field);
    if (change === undefined) {
      return 'cb-removed';
    } else if (!set && change !== undefined) {
      return 'cb-added';
    } else {
      return 'cb-modified';
    }
  };

  useEffect(() => {
    if (onUpdate)
      onUpdate(getData());
  }, [
    streetAddr1,
    streetAddr2,
    streetAddr3,
    countryIsoCd,
    city,
    stateCd,
    zip5,
    zip4,
    province,
    intlPostalCd,
    label,
    contactName,
    contactPhone,
  ]);

  const getErrors = (field: string): string[] => {
    //return get(errors, `${field}._errors`, []);
    const error = get(errors, `${field}._errors`, []);
    return Array.isArray(error) ? error : [];
  };

  const hasErrors = (field: string): boolean => {
    return getErrors(field).length > 0 ? true : false;
  };

  return <>
    <FieldRow>
      <Typography variant="body1">Address</Typography>
      {(showDiff && isChanged('streetAddr1')) &&
        <div><i className={getChangeColor('streetAddr1')}>Current: {getCurrent('streetAddr1') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr1') : ''}
        validation={hasErrors('streetAddr1') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr1').join("\n")}
        onChange={(e) => { setAddress(e.target.value); }}
        value={streetAddr1} />
    </FieldRow>
    <FieldRow>
      <Typography variant="body1">Address 2</Typography>
      {(showDiff && isChanged('streetAddr2')) &&
        <div><i className={getChangeColor('streetAddr2')}>Current: {getCurrent('streetAddr2') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr2') : ''}
        validation={hasErrors('streetAddr2') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr2').join("\n")}
        onChange={(e) => { setAddress2(e.target.value); }}
        value={streetAddr2} />
    </FieldRow>
    <FieldRow>
      <Typography variant="body1">Address 3</Typography>
      {(showDiff && isChanged('streetAddr3')) &&
        <div><i className={getChangeColor('streetAddr3')}>Current: {getCurrent('streetAddr3') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr3') : ''}
        validation={hasErrors('streetAddr3') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr3').join("\n")}
        onChange={(e) => { setAddress3(e.target.value); }}
        value={streetAddr3} />
    </FieldRow>

    <FieldRow>
      <Typography variant="body1">Country</Typography>
      {(showDiff && isChanged('countryIsoCd')) &&
        <div><i className={getChangeColor('countryIsoCd')}>Current: {getCurrent('countryIsoCd') || '(empty)'}</i></div>
      }
      {countries && <Dropdown values={countries}
        disabled={readOnly}
        className={showDiff ? getChangeColor('countryIsoCd') : ''}
        validation={hasErrors('countryIsoCd') ? 'error' : undefined}
        validationMsg={getErrors('countryIsoCd').join("\n")}
        onChange={(value: string) => { setCountry(value); }}
        value={countryIsoCd} />}
    </FieldRow>

    <FieldRow>
      <Typography variant="body1">City</Typography>
      {(showDiff && isChanged('city')) &&
        <div><i className={getChangeColor('city')}>Current: {getCurrent('city') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('city') : ''}
        validation={hasErrors('city') ? 'error' : undefined}
        validationMsg={getErrors('city').join("\n")}
        onChange={(e) => { setCity(e.target.value); }}
        value={city} />
    </FieldRow>
    {isUS(countryIsoCd || '') ?
      <div>
        <FieldRow>
          <Typography variant="body1">State</Typography>
          {(showDiff && isChanged('stateCd')) &&
            <div><i className={getChangeColor('stateCd')}>Current: {getCurrent('stateCd') || '(empty)'}</i></div>
          }
          <Dropdown disabled={readOnly} values={usStateOptions}
            className={showDiff ? getChangeColor('stateCd') : ''}
            validation={hasErrors('stateCd') ? 'error' : undefined}
            validationMsg={getErrors('stateCd').join("\n")}
            onChange={(value: string) => { setState(value); }}
            value={stateCd} />
        </FieldRow>
        <FieldRow container>
          <Grid item xs={6} style={{ paddingRight: '5px' }}>
            <Typography variant="body1">Zip (5 Digit)</Typography>
            {(showDiff && isChanged('zip5')) &&
              <div><i className={getChangeColor('zip5')}>Current: {getCurrent('zip5') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('zip5') : ''}
              validation={hasErrors('zip5') ? 'error' : undefined}
              validationMsg={getErrors('zip5').join("\n")}
              onChange={(e) => { setZip5(e.target.value); }}
              value={zip5} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1">Zip (4 Digit)</Typography>
            {(showDiff && isChanged('zip4')) &&
              <div><i className={getChangeColor('zip4')}>Current: {getCurrent('zip4') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('zip4') : ''}
              validation={hasErrors('zip4') ? 'error' : undefined}
              validationMsg={getErrors('zip4').join("\n")}
              onChange={(e) => { setZip4(e.target.value); }}
              value={zip4} />
          </Grid>
        </FieldRow>
      </div>
      :
      <div>
        <FieldRow>
          <Typography variant="body1">Province</Typography>
          {(showDiff && isChanged('province')) &&
            <div><i className={getChangeColor('province')}>Current: {getCurrent('province') || '(empty)'}</i></div>
          }
          <Input
            disabled={readOnly}
            className={showDiff ? getChangeColor('province') : ''}
            validation={hasErrors('province') ? 'error' : undefined}
            validationMsg={getErrors('province').join("\n")}
            onChange={(e) => { setProvince(e.target.value); }}
            value={province} />
        </FieldRow>
        <FieldRow>
          <Typography variant="body1">Postal Code</Typography>
          {(showDiff && isChanged('intlPostalCd')) &&
            <div><i className={getChangeColor('intlPostalCd')}>Current: {getCurrent('intlPostalCd') || '(empty)'}</i></div>
          }
          <Input
            disabled={readOnly}
            className={showDiff ? getChangeColor('intlPostalCd') : ''}
            validation={hasErrors('intlPostalCd') ? 'error' : undefined}
            validationMsg={getErrors('intlPostalCd').join("\n")}
            onChange={(e) => { setIntlPostalCd(e.target.value); }}
            value={intlPostalCd} />
        </FieldRow>
      </div>
    }
    { additionalInfo &&
        <>
          <FieldRow>
            <FieldLabel>
              {customLabels?.label || 'Label'}
            </FieldLabel>
            <Input
                disabled={readOnly}
                className={showDiff ? getChangeColor('label') : ''}
                validation={hasErrors('label') ? 'error' : undefined}
                validationMsg={getErrors('label').join("\n")}
                onChange={(e) => { setLabel(e.target.value); }}
                value={label || ''} />
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              {customLabels?.contactName || 'Contact Name'}
            </FieldLabel>
            <Input
                disabled={readOnly}
                className={showDiff ? getChangeColor('contactName') : ''}
                validation={hasErrors('contactName') ? 'error' : undefined}
                validationMsg={getErrors('contactName').join("\n")}
                onChange={(e) => { setContactName(e.target.value); }}
                value={contactName || ''} />
          </FieldRow>
          <FieldRow>
            <FieldLabel>
              {customLabels?.contactPhone || 'Contact Phone'}
            </FieldLabel>
            <Input
                disabled={readOnly}
                className={showDiff ? getChangeColor('contactPhone') : ''}
                validation={hasErrors('contactPhone') ? 'error' : undefined}
                validationMsg={getErrors('contactPhone').join("\n")}
                onChange={(e) => { setContactPhone(e.target.value); }}
                value={contactPhone || ''} />
          </FieldRow>
        </>
    }
  </>;
};
